/**
 * v3 visual layout builder — recursive tree editor where every node
 * (Container/Row/Column/Slot) reveals a floating toolbar when selected.
 *
 * Selection model: single-node, lifted into the editor context. Click
 * anywhere outside the layout frame clears it.
 */

import { useEffect, useRef } from 'react';
import type { PageLayout } from '@news-cms/shared';
import { EditorProvider, useEditor } from './editor-context';
import { NodeRenderer } from './node-renderer';

interface Props {
  draft: PageLayout;
  onChange: (next: PageLayout) => void;
}

export function PageLayoutEditorV3({ draft, onChange }: Props) {
  return (
    <EditorProvider layout={draft} onChange={onChange}>
      <EditorShell />
    </EditorProvider>
  );
}

function EditorShell() {
  const { layout, select } = useEditor();
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Clear selection on click outside the editor frame.
  useEffect(() => {
    const handler = (e: PointerEvent) => {
      if (!rootRef.current) return;
      if (e.target instanceof Node && !rootRef.current.contains(e.target)) {
        select(null);
      }
    };
    window.addEventListener('pointerdown', handler);
    return () => window.removeEventListener('pointerdown', handler);
  }, [select]);

  return (
    <div ref={rootRef} className="space-y-2">
      <NodeRenderer node={layout.root} isRoot />
    </div>
  );
}
